import React, { useEffect, useState } from "react";
import { backendUrl } from '../config';
import { Box, Paper, Typography, IconButton, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Grid } from "@mui/material";
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { useTranslation } from 'react-i18next';

export default function AppearanceIconsManager() {
  const { t } = useTranslation();
  const [icons, setIcons] = useState([]);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editIcon, setEditIcon] = useState(null);
  const [iconFile, setIconFile] = useState(null);
  const [iconLabel, setIconLabel] = useState("");
  const [iconValue, setIconValue] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    fetch(`${backendUrl}/icons/icons`).then(r => r.json()).then(setIcons);
  }, []);

  const handleDelete = async (value) => {
    if (!window.confirm(t('appearance.icons.deleteConfirm'))) return;
    await fetch(`${backendUrl}/icons/icon/${value}`, { method: "DELETE" });
    setIcons(icons.filter(i => i.value !== value));
  };

  const handleUpload = async () => {
    if (!iconFile) return;
    const value = iconValue || iconFile.name.replace(/\.[^/.]+$/, "");
    const formData = new FormData();
    formData.append("file", iconFile);
    formData.append("label", iconLabel || value);
    formData.append("value", value);
    await fetch(`${backendUrl}/icons/icon`, { method: "PUT", body: formData });
    fetch(`${backendUrl}/icons/icons`).then(r => r.json()).then(setIcons);
    setUploadOpen(false); setIconFile(null); setIconLabel(""); setIconValue("");
  };

  const handleEdit = (icon) => {
    setEditIcon(icon);
    setIconLabel(icon.label);
    setIconValue(icon.value);
    setEditOpen(true);
  };

  const handleEditSubmit = async () => {
    if (!editIcon) return;
    setErrorMsg("");
    const formData = new FormData();
    formData.append("label", iconLabel);
    formData.append("value", editIcon.value);
    formData.append("new_value", iconValue);
    const resp = await fetch(`${backendUrl}/icons/icon`, {
      method: "POST",
      body: formData
    });
    if (resp.status === 409) {
      setErrorMsg(t('appearance.icons.duplicateError'));
      return;
    }
    const data = await resp.json();
    fetch(`${backendUrl}/icons/icons`).then(r => r.json()).then(setIcons);
    setEditOpen(false); setEditIcon(null);
  };

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h5" gutterBottom>{t('appearance.icons.title')}</Typography>
      <Button variant="contained" onClick={() => setUploadOpen(true)} sx={{ mb: 2 }}>{t('appearance.icons.add')}</Button>
      
      <Grid container spacing={2} direction="column">
        {icons.map(icon => (
          <Grid item key={icon.value}>
            <Paper sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
              <img src={icon.icon_data} alt={icon.label} style={{ width: 40, height: 40, objectFit: 'contain' }} />
              <Box flex={1}>
                <Typography>{icon.label}</Typography>
                <Typography variant="caption" color="textSecondary">{icon.value}</Typography>
              </Box>
              <IconButton onClick={() => handleEdit(icon)}><EditIcon /></IconButton>
              <IconButton onClick={() => handleDelete(icon.value)}><DeleteIcon /></IconButton>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Upload Dialog */}
      <Dialog open={uploadOpen} onClose={() => setUploadOpen(false)}>
        <DialogTitle>{t('appearance.icons.add')}</DialogTitle>
        <DialogContent>
          <TextField 
            label={t('appearance.icons.label')} 
            fullWidth 
            value={iconLabel} 
            onChange={e => setIconLabel(e.target.value)} 
            sx={{ mb: 2 }} 
          />
          <TextField 
            label={t('appearance.icons.value')} 
            fullWidth 
            value={iconValue} 
            onChange={e => setIconValue(e.target.value)} 
            sx={{ mb: 2 }} 
          />
          <Button component="label" variant="outlined">
            {t('appearance.icons.fileSelect')}
            <input type="file" accept="image/svg+xml,image/png" hidden onChange={e => setIconFile(e.target.files[0])} />
          </Button>
          {iconFile && <Typography variant="body2">{iconFile.name}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadOpen(false)}>{t('cancel')}</Button>
          <Button onClick={handleUpload} variant="contained" disabled={!iconFile}>{t('save')}</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)}>
        <DialogTitle>{t('appearance.icons.edit')}</DialogTitle>
        <DialogContent>
          <TextField 
            label={t('appearance.icons.label')} 
            value={iconLabel} 
            onChange={e => setIconLabel(e.target.value)} 
            fullWidth 
            sx={{ mb: 2 }} 
          />
          <TextField 
            label={t('appearance.icons.value')} 
            value={iconValue} 
            onChange={e => setIconValue(e.target.value)} 
            fullWidth 
            sx={{ mb: 2 }} 
          />
          {errorMsg && <Typography color="error">{errorMsg}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>{t('cancel')}</Button>
          <Button onClick={handleEditSubmit} variant="contained">{t('save')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
